import { useEffect } from 'react';

import { isValidUrl } from '@/lib/utils';
import { sentryService } from '@/services/index';

// ids straight from your Qualtrics snippet
const QUALTRICS_TOKEN = process.env.REACT_APP_QUALTRICS_ID?.trim() ?? '';
const QUALTRICS_DIV_ID = QUALTRICS_TOKEN ? `ZN_${QUALTRICS_TOKEN}` : '';
const QUALTRICS_SCRIPT_ID = 'qualtrics-siteintercept';
const QUALTRICS_SRC = QUALTRICS_TOKEN
  ? `https://zn${QUALTRICS_TOKEN.toLowerCase()}-skyexperience.siteintercept.qualtrics.com/SIE/?Q_ZID=${QUALTRICS_DIV_ID}`
  : '';

declare global {
  interface Window {
    QSI?: any;
    Qualtrics?: any;
  }
}

export const useQualtrics = (enabled: string | boolean | null) => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasValidConfig = Boolean(QUALTRICS_DIV_ID) && isValidUrl(QUALTRICS_SRC);

    const ensureContainer = () => {
      let div = QUALTRICS_DIV_ID ? document.getElementById(QUALTRICS_DIV_ID) : null;
      if (!div) {
        div = document.createElement('div');
        div.id = QUALTRICS_DIV_ID ?? '';
        div.setAttribute('data-qa', 'qualtrics-container');
        document.body.appendChild(div);
      }
      return div;
    };

    const addScript = () => {
      if (QUALTRICS_SCRIPT_ID && document.getElementById(QUALTRICS_SCRIPT_ID)) return;
      const s = document.createElement('script');
      s.id = QUALTRICS_SCRIPT_ID ?? '';
      s.type = 'text/javascript';
      s.async = true;
      s.src = QUALTRICS_SRC;
      s.onerror = e => {
        sentryService.captureException(
          new Error('Qualtrics script failed to load'),
          e ? { extra: { error: e } } : undefined
        );
      };

      // s.onload = () => {
      //   setTimeout(() => {
      //     if (window.QSI && QUALTRICS_INTERCEPT_ID) {
      //       triggerQualtrics(QUALTRICS_INTERCEPT_ID);
      //     } else if (window.Qualtrics) {
      //       console.log('[Qualtrics Hook] Qualtrics object is available:', window.Qualtrics);
      //     } else {
      //       console.warn('[Qualtrics Hook] Qualtrics global object not found.');
      //     }
      //   }, 500); // 0.5 second delay
      // };
      document.body.appendChild(s);
    };

    const removeScriptAndContainer = () => {
      if (QUALTRICS_SCRIPT_ID) document.getElementById(QUALTRICS_SCRIPT_ID)?.remove();

      if (QUALTRICS_DIV_ID) document.getElementById(QUALTRICS_DIV_ID)?.remove();
    };

    const triggerQualtrics = (interceptID: string) => {
      try {
        window?.QSI?.API?.unload();
        window?.QSI?.API?.load();
        window.QSI?.API?.run();
      } catch (err) {
        sentryService.captureException(err);
      }
    };

    if (enabled && hasValidConfig) {
      ensureContainer();
      const onLoad = () => addScript();
      if (document.readyState !== 'complete') {
        window.addEventListener('load', onLoad, { once: true });
        return () => window.removeEventListener('load', onLoad);
      } else {
        addScript();
      }
    } else {
      removeScriptAndContainer();
    }

    return () => {
      if (!enabled) removeScriptAndContainer();
    };
  }, [enabled]);
};
